/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';
import type { ExpandButtonProps } from '@coursera-web/cds-labs';

import { Button, CardStatusTag, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { sortCourses } from 'bundles/partners-graphql/components/cds/courseUtils';
import type { PartnerCourseCardFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerCourseCardsQuery';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { LEARNING_PRODUCTS } from 'bundles/search-common/constants';

import _t from 'i18n!nls/partners-graphql';

type CourseItem = PartnerCourseCardFragment & {
  rating?: number | null;
  reviewCount?: number | null;
};

const styles = {
  showMoreButton: css`
    width: auto !important;
    max-width: none !important;
    align-self: flex-start;
  `,
  sectionPadding: css`
    padding: var(--cds-spacing-300) 0;
  `,
};

const renderExpandButton = ({ onClick, onKeyDown, defaultLabel }: ExpandButtonProps) => (
  <Button css={styles.showMoreButton} size="small" variant="secondary" onClick={onClick} onKeyDown={onKeyDown}>
    {defaultLabel}
  </Button>
);

type Props = {
  courses: CourseItem[];
  title?: string;
};

const CourseCardsSection = ({ courses, title = _t('Courses') }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const sortedCourses = sortCourses(courses);

  if (sortedCourses.length === 0) {
    return null;
  }

  const cardVariant = isMobile || courses.length <= 2 ? 'list' : 'grid';

  const renderCourseCard = ({ item: course, itemIndex }: { item: CourseItem; itemIndex: number }) => {
    const courseUrl = `/learn/${course.slug}`;

    const isComingSoon = !course.launchedAt && !!course.preEnrollmentEnabledAt;

    return (
      <LearningProductCard
        id={course.id}
        slug={course.slug}
        name={course.name}
        linkProps={{ href: courseUrl }}
        variant={cardVariant}
        productType={LEARNING_PRODUCTS.Course}
        partners={course.partners?.map((partner) => ({
          id: partner.id,
          name: partner.name,
          logoUrl: partner.squareLogo ?? undefined,
        }))}
        ImageComponent={
          course.promoPhotoUrl ? (
            <LearningProductCardImage imageUrl={course.promoPhotoUrl} variant={cardVariant} />
          ) : undefined
        }
        avgProductRating={course.rating ?? undefined}
        numProductRatings={course.reviewCount ?? undefined}
        statusTags={
          isComingSoon
            ? {
                tags: [<CardStatusTag key="coming-soon">{_t('Coming soon')}</CardStatusTag>],
                tagNames: ['Coming soon'],
              }
            : undefined
        }
        cardIndex={itemIndex}
      />
    );
  };

  return (
    <div data-e2e="partner-courses-section">
      <ShowMoreGridSection
        css={styles.sectionPadding}
        title={title}
        items={sortedCourses}
        renderItem={renderCourseCard}
        renderExpandButton={renderExpandButton}
        itemsGridConfig={cardVariant === 'list' ? { xs: 6, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 6, md: 4, lg: 3 }}
        defaultRows={isMobile ? 2 : 1}
        showMoreAmount={8}
        spacing={24}
      />
    </div>
  );
};

export default CourseCardsSection;
